<template>
  <teleport to="body">
  <transition name="lb">
    <div v-if="open" class="lb" @click.self="$emit('close')">
      <button class="lb__btn lb__close" @click="$emit('close')" :aria-label="$t('a11y.close')">&times;</button>

      <button
        v-if="items.length > 1"
        class="lb__btn lb__nav lb__nav--prev"
        @click.stop="go(-1)"
        :aria-label="$t('lightbox.prev')"
      >&#8249;</button>

      <img ref="img" :src="items[index]" class="lb__img" alt="" @click.stop />

      <button
        v-if="items.length > 1"
        class="lb__btn lb__nav lb__nav--next"
        @click.stop="go(1)"
        :aria-label="$t('lightbox.next')"
      >&#8250;</button>

      <div v-if="items.length > 1" class="lb__counter">{{ index + 1 }} / {{ items.length }}</div>
    </div>
  </transition>
  </teleport>
</template>

<script>
import gsap from 'gsap'

export default {
  name: 'MediaLightbox',
  props: {
    open: { type: Boolean, default: false },
    items: { type: Array, default: () => [] }, // array of image src strings
    index: { type: Number, default: 0 }
  },
  emits: ['close', 'update:index'],
  data() {
    return { dir: 1 }
  },
  watch: {
    open(v) {
      document.body.style.overflow = v ? 'hidden' : ''
      if (v) this.$nextTick(() => this.slide())
    },
    index() {
      this.$nextTick(() => this.slide())
    }
  },
  mounted() {
    window.addEventListener('keydown', this.onKey)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKey)
    document.body.style.overflow = ''
  },
  methods: {
    go(dir) {
      if (this.items.length < 2) return
      this.dir = dir
      const n = (this.index + dir + this.items.length) % this.items.length
      this.$emit('update:index', n)
    },
    onKey(e) {
      if (!this.open) return
      if (e.key === 'Escape') this.$emit('close')
      else if (e.key === 'ArrowLeft') this.go(-1)
      else if (e.key === 'ArrowRight') this.go(1)
    },
    slide() {
      const el = this.$refs.img
      if (!el) return
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
      gsap.fromTo(
        el,
        { opacity: 0, x: 44 * this.dir, scale: 0.97 },
        { opacity: 1, x: 0, scale: 1, duration: 0.35, ease: 'power2.out' }
      )
    }
  }
}
</script>

<style lang="scss">
.lb {
  position: fixed; inset: 0; z-index: 4000; display: grid; place-items: center;
  // translucent + blurred so the page shows through softly behind the photo
  background: rgba(8, 15, 35, 0.6); padding: 40px;
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}
.lb__img {
  // Source gallery images are small (≈463×280). Drive the size off the viewport
  // height so they fill the lightbox instead of rendering at their tiny natural
  // size; max-width + object-fit keep the aspect ratio intact on wide images.
  width: auto; height: 85vh;
  max-width: 94vw; max-height: 90vh; border-radius: 10px; object-fit: contain;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5); background: #0b1f4a;
}
.lb__btn {
  position: absolute; border: 0; cursor: pointer; color: #fff;
  background: rgba(255, 255, 255, 0.12); border-radius: 999px;
  display: flex; align-items: center; justify-content: center; transition: background 0.15s, transform 0.15s;
  z-index: 5;   // keep the nav/close above the image so taps land on them
}
.lb__btn:hover { background: rgba(255, 255, 255, 0.28); }
.lb__btn:active { transform: scale(0.94); }
.lb__close { top: 22px; right: 26px; width: 46px; height: 46px; font-size: 30px; line-height: 1; }
.lb__nav { top: 50%; transform: translateY(-50%); width: 56px; height: 56px; font-size: 38px; line-height: 1; padding-bottom: 4px; }
.lb__nav:active { transform: translateY(-50%) scale(0.94); }
.lb__nav--prev { left: 24px; }
.lb__nav--next { right: 24px; }
.lb__counter {
  position: absolute; bottom: 26px; left: 50%; transform: translateX(-50%);
  color: #fff; font-size: 15px; font-weight: 600;
  background: rgba(255, 255, 255, 0.14); padding: 6px 14px; border-radius: 999px;
}
.lb-enter-active, .lb-leave-active { transition: opacity 0.25s ease; }
.lb-enter-from, .lb-leave-to { opacity: 0; }

@media (max-width: 600px) {
  .lb { padding: 16px; }
  // size to the photo (no forced 85vh) → no navy letterbox; blurred bg shows around
  .lb__img { width: 100%; height: auto; max-height: 85vh; background: transparent; box-shadow: none; }
  .lb__nav { width: 44px; height: 44px; font-size: 30px; }
  .lb__nav--prev { left: 8px; }
  .lb__nav--next { right: 8px; }
}
</style>
